import Card from "./card";
import { getImageURL } from "../utils/getImageURL";
import { DecorationImage, Person } from "../queries/aboutUsQuery";
import { useState } from "react";
import Close from "./icons/close";

const cardContainerClasses =
  "flex flex-col h-full w-full rounded-none p-0 justify-center";

export function TeamMemberCard({ member }: { member: Person }) {
  const [showDescription, setShowDescription] = useState<boolean>(false);

  return (
    <div className="flip-card h-[360px] w-full sm:h-[252px]">
      <div className="flip-card-inner h-full w-full">
        <div className="flip-card-front h-full w-full bg-darkblue">
          {frontCard(member, member.avatarDecoration)}
        </div>

        <div
          className={`flip-card-back justify-center shadow-none ${cardContainerClasses} bg-${getBackgroundClass(
            member.archetype?.name!,
          )} `}
        >
          {!showDescription && backCard(member)}

          {showDescription && backCardWithDescription(member)}
        </div>
      </div>
    </div>
  );

  function toggleDescription(): void {
    setShowDescription(!showDescription);
  }

  function frontCard(member: Person, decoration: DecorationImage) {
    return (
      <div
        className={`${cardContainerClasses} bg-no-repeat`}
        style={
          decoration?.image
            ? {
                backgroundImage: `url(${getImageURL(decoration?.image).url()})`,
                backgroundPosition: `${decoration?.positionX} ${decoration?.positionY}`,
                backgroundSize: "40%",
              }
            : undefined
        }
      >
        <div className="z-10 px-[20px] pt-[10px] text-right">
          <div className="text-body-20 leading-[20px] text-white sm:text-body-15">
            <strong>{member.name}</strong>
          </div>
          <div className="sm:text-body-4 text-body-2 text-white">
            {member.title}
          </div>
          {member.annotation && (
            <div className="text-body-13 font-normal italic text-green">
              {member.annotation}
            </div>
          )}
        </div>

        <div className="h-full w-full">
          {member.image && (
            <img
              className="absolute bottom-0 max-h-[430px] sm:max-h-[305px]"
              src={getImageURL(member.image).url()}
              alt={member.name}
            />
          )}
        </div>
      </div>
    );
  }

  function backCardWithDescription(member: Person) {
    return (
      <Card className="flex items-center p-5">
        <div className="pb-3 text-body-13 font-normal">
          <div
            style={{
              overflow: "hidden",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 9,
            }}
          >
            <strong> {member.archetype?.name} </strong>
            {member.archetype?.description}
          </div>
        </div>
        <div
          className="mr-auto flex cursor-pointer items-center"
          onClick={() => toggleDescription()}
        >
          <Close className="mr-2 text-green"></Close>

          <p className="text-body-13">
            <strong>Close</strong>
          </p>
        </div>
      </Card>
    );
  }

  function backCard(member: Person) {
    return (
      <Card className="flex items-center">
        <div className="text-transform: text-[11px] uppercase text-white">
          Pairtree Personality Type
        </div>
        {member.archetype && member.archetype.image && (
          <img
            className="w-[145px] py-4"
            src={getImageURL(member.archetype.image).url()}
            alt={member.archetype.image.alt}
          />
        )}
        <div className="cursor-pointer" onClick={() => toggleDescription()}>
          {getInfoIcon()}
        </div>
      </Card>
    );
  }
}

export default TeamMemberCard;

function getBackgroundClass(arch: string): string {
  const darkblue = "darkblue";
  const teal = "teal";

  if (
    arch === ArchetypeName.HERO ||
    arch === ArchetypeName.NEIGHBOR ||
    arch === ArchetypeName.CREATIVE
  ) {
    return darkblue;
  }

  return teal;
}

function getInfoIcon() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.80023 12.8761V11.4243H7.76246V8.49009H6.80023V7.02979H9.23293V11.409H10.2002V12.8744H6.80025L6.80023 12.8761Z"
        fill="white"
      />
      <path
        d="M8.26903 4.12451C8.52679 4.12628 8.77343 4.22982 8.95523 4.41261C9.13702 4.59533 9.23929 4.84254 9.23973 5.1003C9.2353 5.36322 9.12756 5.61378 8.93991 5.79795C8.75225 5.98212 8.49969 6.08511 8.23671 6.0846C7.98088 6.07746 7.73801 5.97049 7.56009 5.78655C7.38212 5.60255 7.28328 5.35623 7.28471 5.1003C7.29041 4.84181 7.39622 4.59567 7.57983 4.41365C7.76345 4.23158 8.01054 4.12794 8.26903 4.12451Z"
        fill="white"
      />
      <path
        d="M8.52728 4.40397e-05C13.1938 0.0153171 17.0273 3.85224 17.0001 8.48644C16.9983 9.98273 16.6027 11.4521 15.853 12.7471C15.1033 14.0419 14.0261 15.1167 12.7293 15.8632C11.4326 16.6097 9.96224 17.0018 8.4661 17C3.7962 17.0086 -0.0220045 13.1427 9.54564e-05 8.42524C0.0198509 6.17912 0.927842 4.0323 2.52559 2.45357C4.12345 0.874836 6.28103 -0.00718096 8.5273 4.40397e-05H8.52728ZM8.56641 1.60824H8.56635C7.35211 1.59651 6.15646 1.90679 5.10104 2.50744C4.04574 3.10808 3.16843 3.97766 2.55841 5.02755C1.94841 6.07754 1.62756 7.27042 1.62855 8.48479C1.58605 12.2401 4.70215 15.3749 8.46765 15.3834C9.68161 15.3861 10.8747 15.0677 11.9257 14.4603C12.9769 13.8531 13.8486 12.9785 14.4525 11.9255C15.0565 10.8725 15.3711 9.67839 15.3645 8.46444C15.3424 4.69894 12.2824 1.61344 8.56618 1.60834L8.56641 1.60824Z"
        fill="white"
      />
    </svg>
  );
}

export enum ArchetypeName {
  LOVER = "Lover",
  INNOCENT = "Innocent",
  RULER = "Ruler",
  CREATIVE = "Creative",
  MAGICIAN = "Magician",
  REBEL = "Rebel",
  EXPLORER = "Explorer",
  SAGE = "Sage",
  NEIGHBOR = "Neighbor",
  HERO = "Hero",
  CAREGIVER = "Caregiver",
  JESTER = "Jester",
}
